import { z } from 'zod'
import { ImageReferenceSchema } from './image'

const VARIABLE_NAME_RE_SOURCE = '[a-zA-Z][a-zA-Z0-9_]*'
const PURE_LITERAL_RE = /^[^{}]*$/
const SINGLE_VARIABLE_RE = new RegExp(`^\\s*\\{${VARIABLE_NAME_RE_SOURCE}\\}\\s*$`)

export const TextValueSchema = z.object({
  content: z.string().refine(
    s => PURE_LITERAL_RE.test(s) || SINGLE_VARIABLE_RE.test(s),
    { message: 'content must be a pure literal or a single {variableName}' },
  ),
})
export type TextValue = z.infer<typeof TextValueSchema>

/** @deprecated Use TextValueSchema. Kept for migration code that still references the old name. */
export const ValueSourceSchema = TextValueSchema
/** @deprecated Use TextValue. */
export type ValueSource = TextValue

export const RotationSchema = z.union([
  z.literal(0),
  z.literal(90),
  z.literal(180),
  z.literal(270),
])
export type Rotation = z.infer<typeof RotationSchema>

/**
 * Print-time whole-label 90° rotation, used when a label's design is wider
 * than the printer's print head (the head caps width; the feed direction is
 * effectively unbounded). The designer authors in READING orientation; this
 * setting only affects the print/render path. AFFECTS ZPL OUTPUT.
 *   mode 'auto' — rotate only when widthMm exceeds the head and heightMm fits
 *   mode 'on'   — always rotate (print-and-apply stations with a fixed edge)
 *   mode 'off'  — never rotate (falls back to ^PW clamp + PRINT_WIDTH_CLAMPED)
 *   direction   — applied whenever rotation happens (cw = 90°, ccw = 270°)
 */
export const PrintRotationSchema = z.object({
  mode: z.enum(['auto', 'on', 'off']),
  direction: z.enum(['cw', 'ccw']),
})
export type PrintRotation = z.infer<typeof PrintRotationSchema>

export const TextAlignmentSchema = z.enum(['left', 'center', 'right', 'justify'])
export type TextAlignment = z.infer<typeof TextAlignmentSchema>

export const TextWeightSchema = z.enum(['normal', 'bold'])
export type TextWeight = z.infer<typeof TextWeightSchema>

export const TextOverflowSchema = z.enum(['clip', 'truncate', 'ellipsize', 'wrap'])
export type TextOverflow = z.infer<typeof TextOverflowSchema>

export const PlacementSchema = z.object({
  // 'justify' preserves the one capability the (now-dropped, see Task 6)
  // TextComponent.alignment field had that 3-cell placement couldn't express
  // (text justified across the line). The AlignmentGrid picker stays 3×3;
  // 'justify' is reachable only via the alignment-fold migration or
  // programmatic API.
  hAlign: z.enum(['left', 'center', 'right', 'justify']),
  vAlign: z.enum(['top', 'middle', 'bottom']),
})
export type Placement = z.infer<typeof PlacementSchema>

export const TextFormatSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('plain') }),
  z.object({ kind: z.literal('date'), pattern: z.string().min(1) }),
  z.object({
    kind: z.literal('number'),
    decimals: z.number().int().nonnegative().optional(),
    separator: z.enum(['none', 'comma', 'dot', 'space']).optional(),
    suffix: z.string().optional(),
  }),
])
export type TextFormat = z.infer<typeof TextFormatSchema>

export const TextComponentSchema = z.object({
  type: z.literal('text'),
  id: z.string(),
  value: TextValueSchema,
  fontId: z.string().optional(),
  sizePt: z.number().positive().optional(),
  weight: TextWeightSchema.optional(),
  rotation: RotationSchema.optional(),
  maxLines: z.number().int().positive().optional(),
  overflow: TextOverflowSchema.optional(),
  lineHeight: z.number().positive().optional(),
  format: TextFormatSchema.optional(),
  placement: PlacementSchema.optional(),
})
export type TextComponent = z.infer<typeof TextComponentSchema>

export const ImageFitSchema = z.enum(['contain', 'cover', 'stretch'])
export type ImageFit = z.infer<typeof ImageFitSchema>

export const ImageComponentSchema = z.object({
  type: z.literal('image'),
  id: z.string(),
  source: ImageReferenceSchema,
  fit: ImageFitSchema.optional(),
  rotation: RotationSchema.optional(),
  placement: PlacementSchema.optional(),
  // Mono conversion cutoff: pixels with luminance < threshold print black.
  // Adjustable per image; default applied at rasterization time (128).
  threshold: z.number().int().min(0).max(255).optional(),
})
export type ImageComponent = z.infer<typeof ImageComponentSchema>

export const SymbologySchema = z.enum([
  'code-128',
  'code-39',
  'ean-13',
  'ean-8',
  'upc-a',
  'itf',
  'gs1-128',
  'qr',
  'datamatrix',
  'pdf417',
  'aztec-cc',
])
export type Symbology = z.infer<typeof SymbologySchema>

export const GtinChecksumModeSchema = z.enum(['auto', 'validated', 'skip'])
export type GtinChecksumMode = z.infer<typeof GtinChecksumModeSchema>

export const SymbolDataStructureSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('raw'), value: TextValueSchema }),
  z.object({
    kind: z.literal('gtin'),
    value: TextValueSchema,
    checksum: GtinChecksumModeSchema.optional(),
  }),
  z.object({
    kind: z.literal('gs1-ai'),
    elements: z.array(
      z.object({
        ai: z
          .string()
          .regex(/^\d{2,4}$/, 'GS1 AI must be 2–4 digits'),
        value: TextValueSchema,
      })
    ),
  }),
  z.object({ kind: z.literal('url'), value: TextValueSchema }),
])
export type SymbolDataStructure = z.infer<typeof SymbolDataStructureSchema>

export const SymbolComponentSchema = z.object({
  type: z.literal('symbol'),
  id: z.string(),
  symbology: SymbologySchema,
  dataStructure: SymbolDataStructureSchema,
  humanReadable: z.boolean().optional(),
  /**
   * Size (pt) of the human-readable line under the bars. Only meaningful for
   * code-128 / gs1-128 (the pipeline emits its own ^A0 line for those —
   * EAN-13 keeps the native ^BE interpretation line, QR has none).
   * Unset → DEFAULT_HRI_SIZE_PT.
   */
  humanReadableSizePt: z.number().positive().optional(),
  rotation: RotationSchema.optional(),
  moduleWidthMm: z.number().positive().optional(),
  heightMm: z.number().positive().optional(),
  placement: PlacementSchema.optional(),
})
export type SymbolComponent = z.infer<typeof SymbolComponentSchema>

export const SeparatorOrientationSchema = z.enum(['horizontal', 'vertical'])
export type SeparatorOrientation = z.infer<typeof SeparatorOrientationSchema>

/** @deprecated use Split.divider for boundary lines. Kept for backward compat. */
export const SeparatorComponentSchema = z.object({
  type: z.literal('separator'),
  id: z.string(),
  orientation: SeparatorOrientationSchema,
  thicknessMm: z.number().positive().optional(),
  style: z.enum(['solid', 'dashed', 'dotted']).optional(),
  placement: PlacementSchema.optional(),
})
export type SeparatorComponent = z.infer<typeof SeparatorComponentSchema>

export const LocalizedVariantSchema = z.object({
  locale: z.string().min(2),
  value: TextValueSchema,
})
export type LocalizedVariant = z.infer<typeof LocalizedVariantSchema>

export const LocalizedTextComponentSchema = z.object({
  type: z.literal('localized-text'),
  id: z.string(),
  variants: z.array(LocalizedVariantSchema).min(1),
  fontId: z.string().optional(),
  sizePt: z.number().positive().optional(),
  weight: TextWeightSchema.optional(),
  rotation: RotationSchema.optional(),
  placement: PlacementSchema.optional(),
})
export type LocalizedTextComponent = z.infer<typeof LocalizedTextComponentSchema>

export const LocalizedRichTextLayoutSchema = z.enum([
  'stacked',
  'columns',
  'grid',
])
export type LocalizedRichTextLayout = z.infer<typeof LocalizedRichTextLayoutSchema>

export const LocalizedRichTextComponentSchema = z.object({
  type: z.literal('localized-rich-text'),
  id: z.string(),
  variants: z.array(LocalizedVariantSchema).min(1),
  layout: LocalizedRichTextLayoutSchema.optional(),
  fontId: z.string().optional(),
  sizePt: z.number().positive().optional(),
  placement: PlacementSchema.optional(),
})
export type LocalizedRichTextComponent = z.infer<typeof LocalizedRichTextComponentSchema>

export const LeafPrimitiveSchema = z.discriminatedUnion('type', [
  TextComponentSchema,
  ImageComponentSchema,
  SymbolComponentSchema,
  SeparatorComponentSchema,
  LocalizedTextComponentSchema,
  LocalizedRichTextComponentSchema,
])
export type LeafPrimitive = z.infer<typeof LeafPrimitiveSchema>
